import React, { useState } from 'react';
import { Dropdown, Button, Ref, Popup, Header, Portal } from 'semantic-ui-react';
import { CloseableContent } from 'dyl-components';

import { StringUtils } from "utils";
import LostForm from "shared/forms/LostForm";

import './ConversionControls.scss';
import { useNavigate } from 'react-router-dom';
import ConvertAccountToCustomerForm from 'shared/forms/ConvertAccountToCustomerForm';
import { useSelector } from 'react-redux';
import RestrictedView from 'shared/RestrictedView';
import { USER_PERMISSIONS } from 'dyl-components';

export const PopupForm = ({
    account,
    statusRef,
    conversionModalOpen,
    isConversionFormModified = false,
    setIsConversionFormModified,
    refresh,
    onClose
}) => {
    const getPopupContent = (conversionModalOpen) => {
        switch (conversionModalOpen) {
            case 'customer':
                return (
                    <React.Fragment>
                        <Header color='primary'>Convert to Customer</Header>
                        <ConvertAccountToCustomerForm 
                            existing_account_id={account.id}
                            onClose={onClose}
                            size={"small"}
                            onModify={(isModified) => { setIsConversionFormModified(isModified) }}
                            refresh={refresh}
                            owner_id={account?.primary_person_id}
                        />
                    </React.Fragment>
                )
            case 'pause':
                return (
                    <React.Fragment>
                        <div className={"Account__lost-form"}>
                            <Header color='primary'>Pause Customer</Header>
                            <LostForm
                                account_id={account.id}
                                onClose={onClose}
                                type={'paused'}
                                account_stage={'customer'}
                                refresh={refresh}
                            />
                        </div>
                    </React.Fragment>
                )
            case 'cancel':
                return (
                    <React.Fragment>
                        <div className={"Account__lost-form"}>
                            <Header color='primary'>Cancel Customer</Header>
                            <LostForm
                                account_id={account.id}
                                onClose={onClose}
                                type={'canceled'}
                                account_stage={'customer'}
                                refresh={refresh}
                            />
                        </div>
                    </React.Fragment>
                )
            default:
                return null;
        }
    }

    return (
        <Popup
            style={{ zIndex: '5' }}
            content={(
                <CloseableContent onClose={onClose}>
                    {getPopupContent(conversionModalOpen)}
                </CloseableContent>
            )}
            open={conversionModalOpen && conversionModalOpen !== 'reactivate'}
            position='bottom center'
            context={statusRef}
            closeOnDocumentClick={!isConversionFormModified}
            onClose={() => {
                if (conversionModalOpen !== "pause" && conversionModalOpen !== "cancel") {
                    onClose();
                }
            }}
        />
    );
}

export const StatusDropdown = ({ account, onStatusDropdownSelect }) => {
    const statusRef = React.createRef();
    const [position, setPosition] = useState({ x: 0, y: 0, width: 0 });
    const [isOpen, setIsOpen] = useState(false);
    
    const getActionText = () => {
        if (account.account_status) {
            return StringUtils.capitalize(account.account_status);
        }
        return 'Convert';
    }

    function getStatusClass() {
        if (account.account_status) {
            if (account.account_status === "active") {
                return "ConversionControls__StatusDropdown--active"
            }
            return `ConversionControls__StatusDropdown--${account.account_status}`
        }
        return 'ConversionControls__StatusDropdown--convert'
    }

    const navigate = useNavigate();
    const isDyl = useSelector(state => state.auth.dyl_employee)

    return (
        <div className='ConversionControls'>
            <Ref innerRef={statusRef}>
                <Dropdown
                    className={`ConversionControls__StatusDropdown ${getStatusClass()}`}
                    as={Button}
                    text={getActionText()}
                    onClick={() => {
                        if (statusRef.current) {
                            const position = statusRef.current.getBoundingClientRect();
                            const scrollY = window.scrollY;
                            setPosition({ x: position.left, y: position.top + scrollY + 15, width: position.width });
                            setIsOpen(!isOpen)
                        }
                    }}
                    onBlur={() => setIsOpen(false)}
                >
                    <Portal open>
                        <Dropdown open={isOpen} style={{ position: 'absolute', zIndex: 999, left: position.x, top: position.y, visibility: 'hidden' }} onClose={() => { setIsOpen(false); }}>
                            <Dropdown.Menu {...(position.width ? { style: { width: position.width } } : {})} >
                                {(!account.account_status || account.account_status === '') && (
                                    <RestrictedView userPermission={USER_PERMISSIONS.ACCOUNT_EDIT} type="disabled" assignmentIds={[(account?.owner_id || 0), (account?.co_owner_id || 0)]} userRestrict> 
                                        <Dropdown.Item onClick={() => {
                                            onStatusDropdownSelect('customer', statusRef, account);
                                            setIsOpen(false);
                                        }}>
                                            Convert to Customer
                                        </Dropdown.Item>
                                    </RestrictedView>
                                )}
                                {(account.account_status === 'paused' || account.account_status === 'canceled') && (
                                    <RestrictedView userPermission={account.fund_type !== 'trial' ? USER_PERMISSIONS.ACCOUNT_EDIT : USER_PERMISSIONS.ORDERS_ADD} type="disabled" assignmentIds={account.fund_type !== 'trial' ? [(account?.owner_id || 0), (account?.co_owner_id || 0)] : []} userRestrict> 
                                        <Dropdown.Item onClick={() => {
                                            navigate(`/account/${account.id}`, { state: { isReactivation: true }});
                                        }}>
                                            {account.fund_type === 'trial' ? 'Upgrade' : 'Reactivate'}
                                        </Dropdown.Item>
                                    </RestrictedView>
                                )}
                                {!isDyl && account.account_status === 'active' && [
                                    <Dropdown.Item onClick={() => {
                                        onStatusDropdownSelect('pause', statusRef, account);
                                        setIsOpen(false);
                                    }}>
                                        Pause Customer
                                    </Dropdown.Item>
                                ]}
                                {(account.account_status === 'active' || account.account_status === 'paused') && (
                                    <RestrictedView userPermission={USER_PERMISSIONS.ACCOUNT_EDIT} type="disabled" assignmentIds={[(account?.owner_id || 0), (account?.co_owner_id || 0)]} userRestrict> 
                                        <Dropdown.Item onClick={() => {
                                            onStatusDropdownSelect('cancel', statusRef, account);
                                            setIsOpen(false);
                                        }}>
                                            Cancel Customer
                                        </Dropdown.Item>
                                    </RestrictedView>
                                )}
                            </Dropdown.Menu>
                        </Dropdown>
                    </Portal>
                </Dropdown>
            </Ref>
        </div>
    );
}
